<template>
    <input
        ref="inputRef"
        type="text"
        :disabled="disabled"
        @blur="$emit('blur', $event)"
        @focus="$emit('focus', $event)"
    >
</template>

<script lang="ts">
    // PRODUCT-26110 Task 8b Step 5b: vue-currency-input 1 (an Options-API `<currency-input>`
    // component plus a custom directive using Vue-2-only `bind`/`componentUpdated` hooks --
    // the CUSTOM_DIR compat trigger Task 5 deferred) -> 3, which drops the pre-built
    // component/directive entirely in favor of a `useCurrencyInput` composable meant to
    // decorate a caller-owned `<input>`. This is the shared drop-in replacement for the old
    // library component: same `value`/`currency`/`locale`/`precision`/`valueAsInteger`/
    // `disabled` props, same `input`/`blur`/`focus` events, so every existing call site
    // (CriterionCurrencyInput.vue x2 in tprm-portal, CurrencyFormInput.vue in ido-facing)
    // needed only an import-path change, not a template rewrite.
    //
    // CAUTION for future maintainers: `useCurrencyInput`'s own auto-emit logic hard-codes
    // Vue 3 `v-model` conventions -- it reads the CURRENT component instance's `modelValue`
    // prop and auto-emits `update:modelValue` (see its source, `useCurrencyInput.ts`). Since
    // this wrapper intentionally keeps the OLD `value`/`input` names (to avoid touching call
    // sites), `autoEmit` is passed as `false` below and both directions are wired manually:
    // `numberValue` is watched to emit `input` ourselves, and `setValue` is called by us to
    // push a value in.
    //
    // Pushing a value in is only possible once the composable has built its underlying
    // instance, which it does from `inputRef` on mount. Seeding is therefore driven by
    // `inputRef` becoming available rather than by the value prop alone -- see the watchers
    // below for why the obvious `immediate` version does not work.
    import { defineComponent, nextTick, PropType, watch } from 'vue';

    import { useCurrencyInput, ValueScaling, CurrencyInputOptions } from 'vue-currency-input';

    export default defineComponent({
        name: 'CurrencyInput',

        props: {
            value: {
                type: Number as PropType<number | null>,
                default: null,
            },
            currency: {
                type: String,
                default: null,
            },
            locale: {
                type: String,
                default: 'en-US',
            },
            precision: {
                type: Number,
                default: undefined,
            },
            valueAsInteger: {
                type: Boolean,
                default: false,
            },
            disabled: {
                type: Boolean,
                default: false,
            },
        },

        emits: [ 'input', 'blur', 'focus' ],

        setup(props, { emit }) {
            const buildOptions = (): CurrencyInputOptions => ({
                currency: props.currency as string,
                locale: props.locale,
                precision: props.precision,
                valueScaling: props.valueAsInteger ? ValueScaling.precision : undefined,
            });

            const { inputRef, numberValue, setValue, setOptions } = useCurrencyInput(buildOptions(), false);

            // `input` means the user changed the amount. Seeding also moves `numberValue` --
            // `setValue` formats, which re-parses the display back into a number -- so a value
            // that does not survive the configured precision would otherwise report itself as
            // an edit nobody made. With `precision: 0`, seeding a stored 1500.5 renders
            // "$1,501" and would emit 1501, marking the surrounding form dirty on open and
            // letting a later save persist a rounded amount the user never typed.
            let seeding = false;

            watch(numberValue, (value) => {
                if (!seeding && value !== props.value) {
                    emit('input', value);
                }
            });

            // Cleared on the next tick rather than straight after `setValue`, because the
            // watcher above is pre-flush: it runs from the scheduler queue, after this
            // function has already returned.
            const seed = (value: number | null): void => {
                seeding = true;
                setValue(value);
                void nextTick(() => {
                    seeding = false;
                });
            };

            // Seed off `inputRef`, NOT off an `immediate` watcher on the value. `immediate:
            // true` runs its callback synchronously as the watcher is created -- during
            // setup, before mount -- and `flush: 'post'` governs only later reactive
            // triggers, never that first call. The composable has no instance to write to
            // that early, so the seed is dropped; and a value that never changes again is
            // never re-pushed, so the field stays empty for the life of the component even
            // though the value is present the whole time.
            watch(inputRef, (el) => {
                if (el) {
                    seed(props.value);
                }
            }, { flush: 'post' });
            watch(() => props.value, (value) => setValue(value), { flush: 'post' });

            watch(
                () => [ props.currency, props.locale, props.precision, props.valueAsInteger ],
                () => setOptions(buildOptions()),
            );

            return { inputRef };
        },
    });
</script>
